import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/platform/lib/cn";

// eslint-disable-next-line react-refresh/only-export-components
export const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold",
    // Transições simples: só cor/border. Sem ease-spring (overshoot causa flicker),
    // sem box-shadow (repaint caro), sem transform (active:scale fica instantâneo).
    "transition-colors duration-150 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--color-ring] focus-visible:ring-offset-2 focus-visible:ring-offset-[--color-background]",
    "disabled:pointer-events-none disabled:opacity-50",
    "active:scale-[0.98]",
    "tracking-[-0.005em]",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-[color:var(--color-accent)] text-white",
          "shadow-[var(--shadow-accent)]",
          "hover:bg-[color:var(--color-accent-deep)]",
        ].join(" "),
        gradient: [
          "bg-gradient-accent text-white",
          "shadow-[var(--shadow-accent)]",
          "border border-white/15",
          "hover:brightness-105",
        ].join(" "),
        solid: [
          "bg-[color:var(--color-foreground)] text-[color:var(--color-background)]",
          "shadow-[var(--shadow-sm)]",
          "hover:opacity-90",
        ].join(" "),
        accent: [
          "bg-[color:var(--color-accent)] text-white",
          "shadow-[var(--shadow-accent)]",
          "hover:bg-[color:var(--color-accent-deep)]",
        ].join(" "),
        secondary: [
          "border border-[--color-border] bg-[color:var(--color-card)]",
          "text-[color:var(--color-foreground)]",
          "hover:bg-[color:var(--color-muted)] hover:border-[--color-accent]/40",
        ].join(" "),
        ghost:
          "text-[color:var(--color-foreground)] hover:bg-[color:var(--color-muted)]",
        outline: [
          "border-2 border-[--color-border] bg-transparent",
          "text-[color:var(--color-foreground)]",
          "hover:border-[color:var(--color-accent)] hover:bg-[color:var(--color-accent-soft)]",
          "hover:text-[color:var(--color-accent-soft-foreground)]",
        ].join(" "),
        onDark: [
          "bg-white text-[color:var(--color-accent-deep)]",
          "shadow-[var(--shadow-lg)]",
          "hover:bg-white/90",
        ].join(" "),
        onDarkOutline: [
          "border-2 border-white/40 bg-white/12 backdrop-blur-sm text-white",
          "hover:bg-white/22 hover:border-white/70",
        ].join(" "),
        onDarkGhost: "text-white hover:bg-white/15",
        destructive: [
          "bg-[color:var(--color-danger)] text-white",
          "shadow-[var(--shadow-pink)]",
          "hover:brightness-110",
        ].join(" "),
        link: [
          "text-[color:var(--color-accent)] underline underline-offset-4",
          "decoration-[--color-accent]/40 hover:decoration-[--color-accent]",
          "px-0 h-auto",
        ].join(" "),
      },
      size: {
        sm: "h-8 rounded-full px-3.5 text-[length:var(--text-caption)]",
        md: "h-10 rounded-full px-4 text-[length:var(--text-body-sm)]",
        lg: "h-12 rounded-full px-5 text-[length:var(--text-body-sm)]",
        xl: "h-14 rounded-full px-7 text-[length:var(--text-body)]",
        icon: "h-10 w-10 rounded-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const content = asChild ? (
      children
    ) : (
      <>
        {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
        {children}
      </>
    );
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = "Button";
